/**
 * interactive-deduction (Phase 3-3, Task 6.4) Subtle variant。
 *
 * 控えめな UI。中央に小さなダイアログで矛盾を淡々と提示し、記憶リストから選ぶだけ。
 * 派手演出なし、色合いは既存 PlayerScreen に溶け込む slate 系。
 */

import { useState } from "react";
import { EvidenceSelector } from "./EvidenceSelector";
import { HpIndicator } from "./HpIndicator";
import type { DeductionPanelVariantProps } from "./types";

export function DeductionPanelSubtle({
  contradictions,
  memories,
  attempts,
  onSubmit,
  disabled,
}: DeductionPanelVariantProps): JSX.Element {
  const [selectedContradictionId, setSelectedContradictionId] = useState<
    string | null
  >(contradictions[0]?.id ?? null);
  const [selectedMemoryKey, setSelectedMemoryKey] = useState<string | null>(
    null,
  );

  return (
    <section
      className="flex flex-col gap-3 p-4 bg-slate-800/80 border border-slate-700 rounded-md"
      data-testid="deduction-panel"
      data-variant="subtle"
    >
      <header className="flex justify-between items-center">
        <p className="text-sm text-slate-300">
          気になる点がある記憶を一つ選んで示してみる。
        </p>
        <HpIndicator attempts={attempts} />
      </header>

      <div
        className="flex flex-col gap-1"
        role="radiogroup"
        aria-label="引っかかる点"
      >
        {contradictions.map((c) => (
          <button
            key={c.id}
            type="button"
            role="radio"
            aria-checked={c.id === selectedContradictionId}
            disabled={disabled}
            onClick={() => setSelectedContradictionId(c.id)}
            data-testid={`contradiction-${c.id}`}
            className={[
              "text-left px-2 py-1.5 text-sm rounded",
              c.id === selectedContradictionId
                ? "text-slate-100 bg-slate-700/60"
                : "text-slate-400 hover:text-slate-200",
            ].join(" ")}
          >
            — {c.text}
          </button>
        ))}
      </div>

      <EvidenceSelector
        fragments={memories}
        selectedKey={selectedMemoryKey}
        onSelect={setSelectedMemoryKey}
        disabled={disabled}
      />

      <button
        type="button"
        disabled={
          disabled ||
          selectedContradictionId === null ||
          selectedMemoryKey === null
        }
        onClick={() => {
          if (selectedContradictionId !== null && selectedMemoryKey !== null) {
            onSubmit({
              contradictionId: selectedContradictionId,
              memoryFragmentKey: selectedMemoryKey,
            });
          }
        }}
        data-testid="deduction-submit"
        className="self-end px-4 py-1.5 bg-slate-600 hover:bg-slate-500 text-slate-100 text-sm rounded disabled:opacity-40"
      >
        示す
      </button>
    </section>
  );
}
